import { supabase } from "@/integrations/supabase/client";

/**
 * Request batching for the social data shown on activity cards.
 *
 * The /activities page renders dozens of cards, each of which needs the same
 * kinds of rows (participants for a date, enrollments, profiles, the viewer's
 * friends). Fetching those per card meant ~10 network round-trips per card,
 * which is why avatars took seconds to appear.
 *
 * Everything here collects the ids requested inside a short window and issues
 * ONE query per table using `.in(...)`, then hands each caller its slice.
 */

const WINDOW_MS = 30;

interface Waiter<T> {
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

class KeyedBatcher<T> {
  private queue = new Map<string, Waiter<T>[]>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private cache = new Map<string, T>();

  constructor(
    private readonly load: (ids: string[]) => Promise<Map<string, T>>,
    private readonly empty: () => T
  ) {}

  clear() {
    this.cache.clear();
  }

  get(id: string): Promise<T> {
    const cached = this.cache.get(id);
    if (cached !== undefined) return Promise.resolve(cached);

    return new Promise<T>((resolve, reject) => {
      const waiters = this.queue.get(id) ?? [];
      waiters.push({ resolve, reject });
      this.queue.set(id, waiters);
      if (!this.timer) this.timer = setTimeout(() => void this.flush(), WINDOW_MS);
    });
  }

  private async flush() {
    this.timer = null;
    const pending = this.queue;
    this.queue = new Map();
    const ids = [...pending.keys()];
    if (ids.length === 0) return;

    try {
      const result = await this.load(ids);
      for (const [id, waiters] of pending) {
        const value = result.get(id) ?? this.empty();
        this.cache.set(id, value);
        waiters.forEach((w) => w.resolve(value));
      }
    } catch (error) {
      for (const [, waiters] of pending) waiters.forEach((w) => w.reject(error));
    }
  }
}

const groupBy = <T>(rows: T[], key: (row: T) => string | null | undefined) => {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    if (!k) continue;
    const list = map.get(k);
    if (list) list.push(row);
    else map.set(k, [row]);
  }
  return map;
};

/* ------------------------------------------------------------------ */
/* Participants (per activity, per date)                               */
/* ------------------------------------------------------------------ */

export interface ParticipantRow {
  id: string;
  user_id: string;
  planned_time: string | null;
  note: string | null;
  created_at: string;
  planned_date: string | null;
  visibility: string | null;
  confidence: string | null;
}

const participantBatchers = new Map<string, KeyedBatcher<ParticipantRow[]>>();

const participantBatcher = (plannedDate: string) => {
  let batcher = participantBatchers.get(plannedDate);
  if (!batcher) {
    batcher = new KeyedBatcher<ParticipantRow[]>(async (activityIds) => {
      const { data, error } = await supabase
        .from("activity_participants")
        .select("id, user_id, planned_time, note, created_at, planned_date, visibility, confidence, activity_id")
        .in("activity_id", activityIds)
        .eq("planned_date", plannedDate);
      if (error) throw error;
      return groupBy((data as any[]) || [], (r) => r.activity_id) as Map<string, ParticipantRow[]>;
    }, () => []);
    participantBatchers.set(plannedDate, batcher);
  }
  return batcher;
};

/** Participants of one activity on one date (batched across all cards). */
export const getParticipants = (activityId: string, plannedDate: string) =>
  participantBatcher(plannedDate).get(activityId);

/* ------------------------------------------------------------------ */
/* Enrollments (per activity)                                          */
/* ------------------------------------------------------------------ */

export interface EnrollmentRow {
  id: string;
  user_id: string;
  visibility: string | null;
  note: string | null;
  confidence: string | null;
  created_at: string;
}

const enrollmentsBatcher = new KeyedBatcher<EnrollmentRow[]>(async (activityIds) => {
  const { data, error } = await supabase
    .from("activity_enrollments")
    .select("id, user_id, visibility, note, confidence, created_at, activity_id")
    .in("activity_id", activityIds);
  if (error) throw error;
  return groupBy((data as any[]) || [], (r) => r.activity_id) as Map<string, EnrollmentRow[]>;
}, () => []);

export const getEnrollments = (activityId: string) => enrollmentsBatcher.get(activityId);

/* ------------------------------------------------------------------ */
/* Enrollment skip dates (per enrollment, per date)                     */
/* ------------------------------------------------------------------ */

const skipBatchers = new Map<string, KeyedBatcher<boolean>>();

const skipBatcher = (skipDate: string) => {
  let batcher = skipBatchers.get(skipDate);
  if (!batcher) {
    batcher = new KeyedBatcher<boolean>(async (enrollmentIds) => {
      const { data, error } = await supabase
        .from("enrollment_skip_dates")
        .select("enrollment_id")
        .in("enrollment_id", enrollmentIds)
        .eq("skip_date", skipDate);
      if (error) throw error;
      const map = new Map<string, boolean>();
      for (const id of enrollmentIds) map.set(id, false);
      for (const row of (data as any[]) || []) map.set(row.enrollment_id, true);
      return map;
    }, () => false);
    skipBatchers.set(skipDate, batcher);
  }
  return batcher;
};

/** Returns the enrollment ids that are skipping this date. */
export const getSkippingEnrollmentIds = async (enrollmentIds: string[], skipDate: string) => {
  if (enrollmentIds.length === 0) return new Set<string>();
  const batcher = skipBatcher(skipDate);
  const flags = await Promise.all(enrollmentIds.map((id) => batcher.get(id)));
  return new Set(enrollmentIds.filter((_, i) => flags[i]));
};

/* ------------------------------------------------------------------ */
/* Profiles (per user)                                                 */
/* ------------------------------------------------------------------ */

export interface ProfileBits {
  id: string;
  first_name: string | null;
  avatar_url: string | null;
  open_to_meet?: boolean | null;
}

const profilesBatcher = new KeyedBatcher<ProfileBits | null>(async (userIds) => {
  const { data, error } = await supabase
    .from("public_profiles")
    .select("id, first_name, avatar_url, open_to_meet")
    .in("id", userIds);
  if (error) throw error;
  const map = new Map<string, ProfileBits | null>();
  for (const row of (data as any[]) || []) map.set(row.id, row as ProfileBits);
  return map;
}, () => null);

/** Profiles for a set of users, batched and cached across all cards. */
export const getProfiles = async (userIds: string[]) => {
  const unique = [...new Set(userIds)];
  const rows = await Promise.all(unique.map((id) => profilesBatcher.get(id)));
  const map = new Map<string, ProfileBits>();
  unique.forEach((id, i) => {
    const row = rows[i];
    if (row) map.set(id, row);
  });
  return map;
};

/* ------------------------------------------------------------------ */
/* Participant counts for a whole date (single RPC)                    */
/* ------------------------------------------------------------------ */

const countPromises = new Map<string, Promise<Map<string, number>>>();

const loadCounts = (plannedDate: string) => {
  let promise = countPromises.get(plannedDate);
  if (!promise) {
    promise = (async () => {
      const { data, error } = await supabase.rpc("get_activities_with_participants_on_date", {
        date_param: plannedDate,
      });
      if (error) throw error;
      const map = new Map<string, number>();
      for (const row of (data as any[]) || []) map.set(row.activity_id, row.participant_count ?? 0);
      return map;
    })().catch((error) => {
      countPromises.delete(plannedDate);
      throw error;
    });
    countPromises.set(plannedDate, promise);
  }
  return promise;
};

/** Participant count for one activity on one date (one RPC per date, shared). */
export const getParticipantCount = async (activityId: string, plannedDate: string) => {
  const counts = await loadCounts(plannedDate);
  return counts.get(activityId) ?? 0;
};

/* ------------------------------------------------------------------ */
/* Viewer's friends (single query, shared)                              */
/* ------------------------------------------------------------------ */

let friendIdsUser: string | null = null;
let friendIdsPromise: Promise<string[]> | null = null;

/** Accepted friend ids of the current user — fetched once, shared everywhere. */
export const getFriendIds = (userId: string) => {
  if (friendIdsUser !== userId || !friendIdsPromise) {
    friendIdsUser = userId;
    friendIdsPromise = (async () => {
      const { data, error } = await supabase
        .from("friend_requests")
        .select("sender_id, receiver_id")
        .eq("status", "accepted")
        .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`);
      if (error) throw error;
      return [
        ...new Set(
          (data || []).map((r) => (r.sender_id === userId ? r.receiver_id : r.sender_id))
        ),
      ];
    })().catch((error) => {
      friendIdsPromise = null;
      throw error;
    });
  }
  return friendIdsPromise;
};

/* ------------------------------------------------------------------ */
/* Interested users (per activity)                                     */
/* ------------------------------------------------------------------ */

const interestedBatcher = new KeyedBatcher<string[]>(async (activityIds) => {
  const { data, error } = await supabase.rpc("get_interested_user_ids_for_activities" as any, {
    _activity_ids: activityIds,
  });
  if (error) throw error;
  const grouped = groupBy((data as any[]) || [], (r) => r.activity_id);
  const map = new Map<string, string[]>();
  for (const [activityId, rows] of grouped) {
    map.set(activityId, [...new Set(rows.map((r: any) => r.user_id).filter(Boolean))] as string[]);
  }
  return map;
}, () => []);

/** Ids of users who starred an activity (batched across all cards). */
export const getInterestedUserIds = (activityId: string) => interestedBatcher.get(activityId);

/* ------------------------------------------------------------------ */
/* Enrollment counts (per activity)                                    */
/* ------------------------------------------------------------------ */

const enrollmentCountBatcher = new KeyedBatcher<number>(async (activityIds) => {
  const { data, error } = await supabase.rpc("get_activity_enrollment_counts" as any, {
    _activity_ids: activityIds,
  });
  if (error) throw error;
  const map = new Map<string, number>();
  for (const row of (data as any[]) || []) map.set(row.activity_id, row.enrollment_count ?? 0);
  return map;
}, () => 0);

/** Number of people enrolled for the term (batched across all cards). */
export const getEnrollmentCount = (activityId: string) => enrollmentCountBatcher.get(activityId);

/* ------------------------------------------------------------------ */
/* Invalidation                                                        */
/* ------------------------------------------------------------------ */

/** Drop cached participation data (call after joining / leaving / enrolling). */
export const invalidateParticipation = () => {
  participantBatchers.forEach((b) => b.clear());
  skipBatchers.forEach((b) => b.clear());
  enrollmentsBatcher.clear();
  enrollmentCountBatcher.clear();
  countPromises.clear();
};

/** Drop cached "interested" data (call after starring / unstarring). */
export const invalidateInterests = () => {
  interestedBatcher.clear();
};

/** Drop cached profile data (call after a name / avatar change). */
export const invalidateProfiles = () => {
  profilesBatcher.clear();
};

/** Drop cached friend data (call after accepting / removing a friend). */
export const invalidateFriends = () => {
  friendIdsPromise = null;
  friendIdsUser = null;
};
